/**
 * Detalhe de ProvisioningJob (REDESIGN 2026-04-26).
 *
 * Fixes vs versao anterior (descobertos em PRD durante teste E2E):
 *   - Polling agora funciona — refetchIntervalInBackground=true (era a causa
 *     real do "tela estatica"; tab perde foco e v5 pausa polling por padrao).
 *   - Titulo usa tenant_spec.project_name (nao mais "Unknown").
 *   - Progress bar usa completed_steps/total_steps reais (nao mais 0%).
 *   - Live indicator pulsando + "ao vivo · ha Xs" para jobs ativos.
 *   - Status badge global colorido com pulse animation quando running.
 *   - Step labels em PT (via stepMeta) com contexto do step_input.
 *   - AWS resource IDs como deep links pro AWS Console.
 *   - Cancel + Retry no menu prominent.
 *   - Failed steps auto-expand com error message COMPLETO (nao truncado).
 *   - Success card com link "Abrir tenant" quando completed.
 *   - Skeleton loader durante carregamento.
 */

import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Card,
  Progress,
  Button,
  Tooltip,
  Anchor,
  Skeleton,
  Alert,
  ThemeIcon,
  Menu,
  ActionIcon,
  Box,
  Divider,
} from '@mantine/core'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { notifications } from '@mantine/notifications'
import {
  IconArrowLeft,
  IconAlertCircle,
  IconRefresh,
  IconX,
  IconDots,
  IconExternalLink,
  IconAlertTriangle,
  IconCircleCheck,
  IconClock,
} from '@tabler/icons-react'
import {
  awsOrgProvisioningApi,
  type ProvisioningJobResponse,
} from '@/api/awsOrgProvisioning'
import { JobStatusBadge } from './JobStatusBadge'
import { JobStepTimeline } from './JobStepTimeline'
import {
  ACTIVE_JOB_STATUSES,
  falhaTerminalDeCriacaoDeConta,
  TERMINAL_JOB_STATUSES,
  formatDuration,
  formatRelative,
  parseStepInput,
} from './stepMeta'

const POLL_MS = 2000

export function ProvisioningJobDetail() {
  const { id } = useParams<{ id: string }>()
  const jobId = parseInt(id ?? '0', 10)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    data: job,
    isLoading,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useQuery<ProvisioningJobResponse>({
    queryKey: ['provisioning-job', jobId],
    queryFn: () => awsOrgProvisioningApi.getJob(jobId),
    enabled: jobId > 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (!status) return POLL_MS
      if (TERMINAL_JOB_STATUSES.has(status)) return false
      return POLL_MS
    },
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  })

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!job || TERMINAL_JOB_STATUSES.has(job.status)) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [job])

  const cancelMutation = useMutation({
    mutationFn: () => awsOrgProvisioningApi.cancelJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provisioning-job', jobId] })
      notifications.show({
        title: 'Job cancelado',
        message: `Job #${jobId} marcado como cancelled.`,
        color: 'orange',
      })
    },
  })

  const retryMutation = useMutation({
    mutationFn: (fromStep: number) => awsOrgProvisioningApi.retryJob(jobId, fromStep),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provisioning-job', jobId] })
      notifications.show({
        title: 'Retry disparado',
        message: `Job #${jobId} retomado.`,
        color: 'blue',
      })
    },
  })

  if (isLoading) return <DetailSkeleton />
  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" icon={<IconAlertCircle />} title="Falha ao carregar job">
          {String((error as Error)?.message ?? error)}
        </Alert>
      </Container>
    )
  }
  if (!job) {
    return (
      <Container size="lg" py="xl">
        <Alert color="yellow" icon={<IconAlertCircle />} title="Job não encontrado">
          Job #{jobId} não existe ou foi removido.
        </Alert>
      </Container>
    )
  }

  const tenantSpec = parseStepInput(job.tenant_spec) as {
    project_name?: string
    direct_environments?: string[]
    is_saas?: boolean
    sub_tenants?: { name: string; environments: string[] }[]
  }
  const projectName = tenantSpec.project_name ?? 'Tenant sem nome'
  const isActive = ACTIVE_JOB_STATUSES.includes(
    job.status as 'pending' | 'running',
  )
  const isTerminal = TERMINAL_JOB_STATUSES.has(job.status)

  const completed = job.completed_steps ?? 0
  const total = job.total_steps ?? 0
  const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0
  const lastFailedStep = (job.steps ?? []).find((s) => s.status === 'failed')
  // Ressalva vem da rota de detalhe. Passo pulado grava status='completed',
  // entao nao ha como derivar isto do `status`. Ver TASK-10214.
  const ressalvas = job.ressalvas ?? []
  const temRessalva = ressalvas.length > 0
  // Falha terminal: repetir a partir DESTE passo e no-op garantido — o
  // CreateAccount ja foi recusado em definitivo e o describe nunca muda de
  // estado. So "Re-rodar do inicio" emite um pedido novo. Ver TASK-10150.
  const falhaTerminal = falhaTerminalDeCriacaoDeConta(lastFailedStep)
  const motivoTerminal =
    'O pedido de criação de conta já foi recusado em definitivo pela AWS. ' +
    'Repetir a partir deste passo não muda nada — use "Re-rodar do início" ' +
    'para emitir um pedido novo.'

  const elapsedSeconds = job.started_at
    ? Math.floor(
        ((isTerminal && job.completed_at
          ? new Date(job.completed_at).getTime()
          : Date.now()) -
          new Date(job.started_at).getTime()) /
          1000,
      )
    : 0
  const updatedSecondsAgo = Math.max(0, Math.floor((now - dataUpdatedAt) / 1000))

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="center">
          <Anchor component={Link} to="/admin/tenants/jobs" c="dimmed" size="sm">
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={14} />
              <span>Voltar para Provisioning Jobs</span>
            </Group>
          </Anchor>
          <Menu shadow="md" position="bottom-end" width={240}>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" aria-label="Mais ações">
                <IconDots size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Job #{job.id}</Menu.Label>
              <Menu.Item
                leftSection={<IconRefresh size={14} />}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                Atualizar agora
              </Menu.Item>
              {lastFailedStep && (
                <Menu.Item
                  leftSection={<IconRefresh size={14} />}
                  color="blue"
                  onClick={() =>
                    retryMutation.mutate(lastFailedStep.step_index)
                  }
                  disabled={retryMutation.isPending || falhaTerminal}
                >
                  Retentar do step #{lastFailedStep.step_index}
                </Menu.Item>
              )}
              {!isActive && job.status !== 'cancelled' && (
                <Menu.Item
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => retryMutation.mutate(0)}
                  disabled={retryMutation.isPending}
                >
                  Re-rodar do início (step #0)
                </Menu.Item>
              )}
              {isActive && (
                <>
                  <Menu.Divider />
                  <Menu.Item
                    leftSection={<IconX size={14} />}
                    color="red"
                    onClick={() => cancelMutation.mutate()}
                    disabled={cancelMutation.isPending}
                  >
                    Cancelar job
                  </Menu.Item>
                </>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>

        <Card withBorder radius="md" p="xl">
          <Stack gap="md">
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                <Group gap="xs" align="baseline">
                  <Text size="xs" c="dimmed" ff="monospace" fw={500}>
                    JOB #{job.id}
                  </Text>
                  {tenantSpec.is_saas && (
                    <Text
                      size="xs"
                      c="dimmed"
                      tt="uppercase"
                      style={{ letterSpacing: 1 }}
                    >
                      · SaaS · {tenantSpec.sub_tenants?.length ?? 0} sub-tenant
                      {(tenantSpec.sub_tenants?.length ?? 0) === 1 ? '' : 's'}
                    </Text>
                  )}
                </Group>
                <Title
                  order={1}
                  style={{
                    fontSize: 32,
                    lineHeight: 1.1,
                    letterSpacing: -0.5,
                  }}
                >
                  {projectName}
                </Title>
                <Group gap="md" mt={4}>
                  {(tenantSpec.direct_environments ?? []).map((env) => (
                    <Text
                      key={env}
                      size="xs"
                      c="dimmed"
                      ff="monospace"
                      tt="uppercase"
                    >
                      {env}
                    </Text>
                  ))}
                </Group>
              </Stack>
              <JobStatusBadge status={job.status} />
            </Group>

            <Stack gap={4}>
              <Group justify="space-between" align="baseline">
                <Group gap={6} align="baseline">
                  <Text size="xl" fw={700} ff="monospace">
                    {completed}
                  </Text>
                  <Text size="sm" c="dimmed" ff="monospace">
                    / {total} steps
                  </Text>
                  <Text size="sm" c="dimmed" ml="xs">
                    ({progressPct}%)
                  </Text>
                </Group>
                <MetaTime
                  job={job}
                  isActive={isActive}
                  elapsedSeconds={elapsedSeconds}
                  updatedSecondsAgo={updatedSecondsAgo}
                />
              </Group>
              <Progress
                value={progressPct}
                size="md"
                radius="sm"
                color={
                  job.status === 'failed'
                    ? 'red'
                    : job.status === 'cancelled'
                      ? 'orange'
                      : job.status === 'completed'
                        ? 'teal'
                        : 'blue'
                }
                animated={isActive}
                striped={isActive}
              />
            </Stack>
          </Stack>
        </Card>

        {job.status === 'failed' && job.error_message && (
          <Alert color="red" radius="md" icon={<IconAlertCircle />} title="Job falhou">
            <Text size="sm" mb="md">
              {job.error_message}
            </Text>
            {lastFailedStep && (
              <>
                {falhaTerminal && (
                  <Text size="sm" c="dimmed" mb="xs">
                    {motivoTerminal}
                  </Text>
                )}
                <Tooltip
                  label={motivoTerminal}
                  disabled={!falhaTerminal}
                  withArrow
                  multiline
                  maw={340}
                >
                  <Button
                    variant="light"
                    color="red"
                    leftSection={<IconRefresh size={14} />}
                    size="sm"
                    onClick={() => retryMutation.mutate(lastFailedStep.step_index)}
                    loading={retryMutation.isPending}
                    disabled={falhaTerminal}
                    data-disabled={falhaTerminal || undefined}
                  >
                    Retentar a partir do step #{lastFailedStep.step_index}
                  </Button>
                </Tooltip>
              </>
            )}
          </Alert>
        )}

        {job.status === 'completed' && (
          <Card
            withBorder
            radius="md"
            p="lg"
            style={{
              borderColor: temRessalva
                ? 'var(--mantine-color-yellow-light-hover)'
                : 'var(--mantine-color-teal-light-hover)',
            }}
          >
            <Group gap="md" align="center">
              <ThemeIcon size={40} radius="xl" color={temRessalva ? 'yellow' : 'teal'}>
                {temRessalva ? <IconAlertTriangle size={22} /> : <IconCircleCheck size={22} />}
              </ThemeIcon>
              <Stack gap={2} style={{ flex: 1 }}>
                <Text fw={600}>
                  {temRessalva
                    ? `Tenant ${projectName} provisionado com ressalva`
                    : `Tenant ${projectName} provisionado`}
                </Text>
                <Text size="sm" c="dimmed">
                  Concluído em {formatDuration(elapsedSeconds)} · {total} steps executados
                </Text>
              </Stack>
              <Button
                component={Link}
                to="/admin/tenants"
                variant="light"
                color={temRessalva ? 'yellow' : 'teal'}
                rightSection={<IconExternalLink size={14} />}
              >
                Abrir tenant
              </Button>
            </Group>
            {/* O passo pulado nao aparece no `status` do job. Sem isto o
                operador le "provisionado" e descobre o que faltou na hora de
                entrar na conta. Ver TASK-10214. */}
            {temRessalva && (
              <Stack gap={4} mt="md">
                <Text size="sm" fw={600}>
                  {ressalvas.length === 1
                    ? 'Um passo não foi executado:'
                    : `${ressalvas.length} passos não foram executados:`}
                </Text>
                {ressalvas.map((r) => (
                  <Text key={r} size="sm" c="dimmed">
                    {r}
                  </Text>
                ))}
                <Text size="xs" c="dimmed" mt={4}>
                  A conta foi criada. O que falta acima precisa ser feito à mão.
                </Text>
              </Stack>
            )}
          </Card>
        )}

        <Card withBorder radius="md" p="lg">
          <Group justify="space-between" mb="md" align="center">
            <Title
              order={4}
              c="var(--mantine-color-text)"
              style={{ fontWeight: 600 }}
            >
              Steps
            </Title>
            <Text size="xs" c="dimmed" ff="monospace">
              {(job.steps ?? []).length} steps
            </Text>
          </Group>
          <Divider mb="md" />
          <JobStepTimeline
            steps={job.steps ?? []}
            onRetryFromStep={(idx) => retryMutation.mutate(idx)}
            retryDisabled={retryMutation.isPending}
          />
        </Card>
      </Stack>
    </Container>
  )
}

function MetaTime({
  job,
  isActive,
  elapsedSeconds,
  updatedSecondsAgo,
}: {
  job: ProvisioningJobResponse
  isActive: boolean
  elapsedSeconds: number
  updatedSecondsAgo: number
}) {
  const startedAt = job.started_at ? new Date(job.started_at) : null
  const completedAt = job.completed_at ? new Date(job.completed_at) : null

  return (
    <Group gap="md" wrap="wrap" justify="flex-end">
      {startedAt && (
        <Group gap={4} wrap="nowrap">
          <IconClock size={12} color="var(--mantine-color-dimmed)" />
          <Text size="xs" c="dimmed" ff="monospace">
            {startedAt.toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        </Group>
      )}
      {completedAt ? (
        <Text size="xs" c="dimmed" ff="monospace">
          duração: {formatDuration(elapsedSeconds)}
        </Text>
      ) : isActive ? (
        <Text size="xs" c="dimmed" ff="monospace">
          em execução: {formatDuration(elapsedSeconds)}
        </Text>
      ) : null}
      {isActive && (
        <Group gap={4} wrap="nowrap">
          <Box
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--mantine-color-blue-6)',
              animation: 'live-dot 1.6s ease-in-out infinite',
            }}
          />
          <Text size="xs" c="dimmed" ff="monospace">
            ao vivo · {formatRelative(updatedSecondsAgo)}
          </Text>
          <style>{`@keyframes live-dot { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }`}</style>
        </Group>
      )}
    </Group>
  )
}

function DetailSkeleton() {
  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Skeleton height={20} width={200} />
        <Card withBorder radius="md" p="xl">
          <Stack gap="md">
            <Group justify="space-between">
              <Stack gap={6} style={{ flex: 1 }}>
                <Skeleton height={12} width={80} />
                <Skeleton height={32} width={240} />
                <Skeleton height={10} width={180} />
              </Stack>
              <Skeleton height={32} width={140} />
            </Group>
            <Skeleton height={12} radius="sm" />
          </Stack>
        </Card>
        <Card withBorder radius="md" p="lg">
          <Stack gap="lg">
            {[0, 1, 2, 3].map((i) => (
              <Group key={i} gap="md">
                <Skeleton circle height={28} />
                <Stack gap={4} style={{ flex: 1 }}>
                  <Skeleton height={12} width="40%" />
                  <Skeleton height={10} width="70%" />
                </Stack>
              </Group>
            ))}
          </Stack>
        </Card>
      </Stack>
    </Container>
  )
}
